<template>
    <empty-message type="bug" class="network-error">
        <template #title>It's not you, it's the network error</template>
        <template #content>
            <p v-if="!hasRecovered">We are experiencing some technical difficulties connecting to the server.</p>
            <p v-if="!hasRecovered && retry === numberOfRetries">
                We are still trying to connect to the server, however You can try to manually refresh this page.
            </p>
            <p v-if="hasRecovered && !returnRoute">
                <router-link :to="{ name: 'Home' }">Click here to go to the homepage</router-link>
            </p>
        </template>
    </empty-message>
</template>

<script lang="ts" setup>
import { apolloClient } from "@/apollo";
import meQuery from "@/apollo/queries/Me.gql";
import EmptyMessage from "@/components/common/EmptyMessage.vue";
import type { Me } from "@/store/modules/auth";
import { UiAction } from "@/store/modules/ui";
import type { ObservableSubscription } from "@apollo/client/utilities";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter, type RouteLocationNormalized } from "vue-router";
import { useStore } from "vuex";

const numberOfRetries = 5;
const retry = ref(0);
const hasRecovered = ref(false);

const router = useRouter();
const store = useStore();

onMounted(() => {
    getUserData();
});

let querySubscription: ObservableSubscription | null = null;
const returnRoute = computed<RouteLocationNormalized>(() => store.state.ui.networkErrorReturnRoute);

function getUserData() {
    querySubscription = apolloClient
        .watchQuery<{ me: Me }>({
            fetchPolicy: "no-cache",
            notifyOnNetworkStatusChange: true,
            query: meQuery,
            pollInterval: retry.value < numberOfRetries ? 5000 : 0
        })
        .subscribe({
            next: ({ data }) => {
                if (data.me) {
                    hasRecovered.value = true;
                    if (returnRoute.value) {
                        router.replace(returnRoute.value);
                        store.commit(UiAction.SetNetworkErrorReturnRoute, null);
                    } else {
                        retry.value += 1;
                    }
                }
            },
            error: error => {
                console.error("error", error);
            }
        });
}

onUnmounted(() => {
    if (querySubscription) {
        querySubscription.unsubscribe();
    }
});
</script>

<style lang="scss" scoped>
.network-error {
    margin: 50px auto;
    text-align: center;

    p {
        margin: 10px 0;
    }
}
</style>
